/**
 * api.js — API client for the mini app.
 *
 * Two hosts:
 *   pe-tgauth (8007)  verifies initData, issues the session
 *   pe-tgapi  (8008)  everything else, behind the capability allowlist
 *
 * The token is held in memory, not localStorage. A webview's storage is
 * shared per-origin and outlives the session; keeping it in a module
 * variable means closing the app discards it. Sessions are an hour anyway,
 * and re-auth is a single silent call against initData Telegram already
 * gave us — there is nothing to gain by persisting it.
 */

import { initData } from "./telegram";

const AUTH_BASE = process.env.REACT_APP_TGAUTH_BASE || "https://t.polyedge.online/tg";
const API_BASE = process.env.REACT_APP_TGAPI_BASE || "https://t.polyedge.online/tgapi";

let token = null;
let userId = null;
let refreshing = null;

export const getUserId = () => userId;

export class Unbound extends Error {
  constructor(connectUrl) {
    super("account not linked");
    this.connectUrl = connectUrl;
  }
}

/**
 * Exchange initData for a session.
 *
 * `bound: false` is not an error — it is a first-time user who has not
 * linked their PolyEdge account yet, and the correct response is the Connect
 * screen rather than an error state.
 */
export async function authenticate() {
  const init = initData();
  if (!init) throw new Error("Open this from Telegram.");

  const r = await fetch(`${AUTH_BASE}/auth`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ init_data: init }),
  });

  if (r.status === 401) {
    // Signature failed or auth_date is stale. Reopening regenerates it.
    throw new Error("Session could not be verified. Close and reopen the app.");
  }
  if (!r.ok) throw new Error("Could not reach PolyEdge.");

  const d = await r.json();
  if (!d.bound) throw new Unbound(d.connect_url);

  token = d.token;
  userId = d.user_id;
  return { userId };
}

/** Single-flight: several 401s in parallel must not trigger several re-auths. */
function refresh() {
  if (!refreshing) {
    refreshing = authenticate().finally(() => { refreshing = null; });
  }
  return refreshing;
}

async function call(method, path, { params, body, retry = true } = {}) {
  if (!token) await refresh();

  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) url.searchParams.set(k, v);
  });

  const r = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (r.status === 401 && retry) {
    token = null;
    await refresh();
    return call(method, path, { params, body, retry: false });
  }

  if (r.status === 403) {
    // The proxy refuses by default, and its message explains why — worth
    // surfacing verbatim rather than replacing with something generic.
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || "Not available from Telegram.");
  }

  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `Request failed (${r.status})`);
  }

  return r.json();
}

export const get = (path, params) => call("GET", path, { params });
export const post = (path, body, params) => call("POST", path, { body, params });

// ── endpoints ───────────────────────────────────────────────────────────────
// Every one of these is on the proxy's allowlist. Anything absent from that
// list returns 403 regardless of what is written here, which is the point.

export const listBots = () => get("/api/bots");
export const getBot = (id) => get(`/api/bot/${id}`);
export const botTrades = (id) => get(`/api/bot/${id}/trades`);
export const stopBot = (id) => post(`/api/bot/${id}/stop`);
export const pauseBot = (id) => post(`/api/bot/${id}/pause`);
export const resumeBot = (id) => post(`/api/bot/${id}/resume`);
export const createBot = (cfg) => post("/api/bot/start", cfg);
export const planLimits = () => get("/api/plan-limits");
export const balance = () => get("/api/wallet/balance");
export const depositAddress = () => get("/api/wallet/deposit-address");
export const subscription = () => get("/api/subscription/status");

export async function unlink() {
  const r = await fetch(`${AUTH_BASE}/unbind`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error("Could not unlink.");
  token = null;
  userId = null;
  return true;
}

/**
 * Poll a loader on an interval.
 *
 * Polling, not WebSocket. A webview is backgrounded constantly — minimised,
 * phone locked, chat switched — and reconnect logic in that environment is a
 * great deal of complexity for data that is fine on a timer. It also keeps
 * socket.io out of the bundle.
 *
 * document.hidden gates it so a backgrounded app stops hitting the API.
 */
export function poll(fn, ms = 10000) {
  let stopped = false;
  let timer = null;

  const tick = async () => {
    if (stopped) return;
    if (!document.hidden) {
      try { await fn(); } catch { /* keep polling; a transient failure is not fatal */ }
    }
    timer = setTimeout(tick, ms);
  };

  tick();
  return () => { stopped = true; if (timer) clearTimeout(timer); };
}
